/**
 * Responsive utility functions for fluid typography and scaling
 */

/**
 * Creates a fluid value using CSS clamp()
 * @param minSize - Minimum size in rem
 * @param maxSize - Maximum size in rem
 * @param minViewport - Minimum viewport width in px (default: 320)
 * @param maxViewport - Maximum viewport width in px (default: 1920)
 * @returns CSS clamp() string
 */
export const fluidValue = (
  minSize: number,
  maxSize: number,
  minViewport = 320,
  maxViewport = 1920
): string => {
  const slope = (maxSize - minSize) / (maxViewport - minViewport);
  const yAxisIntersection = -minViewport * slope + minSize;
  
  return `clamp(${minSize}rem, ${yAxisIntersection}rem + ${slope * 100}vw, ${maxSize}rem)`;
};

/**
 * Convert pixels to rem
 * @param px - Pixel value
 * @param base - Base font size (default: 16)
 * @returns rem value
 */
export const pxToRem = (px: number, base = 16): string => {
  return `${px / base}rem`;
};

/**
 * Creates viewport-width based value for proportional scaling
 * @param targetPx - Target pixel value at 1920px viewport
 * @param minPx - Minimum pixel value (optional, defaults to 60% of target)
 * @returns Viewport width calculation
 */
export const vwScale = (targetPx: number, minPx?: number): string => {
  // Calculate the percentage of viewport width at 1920px
  const vwValue = (targetPx / 1920) * 100;
  const minValue = minPx ?? targetPx * 0.5; // tighter minimums
  
  // Use max() to ensure minimum size at smaller viewports
  return `max(${minValue}px, ${vwValue}vw)`;
};

/**
 * Layout dimensions that scale with viewport
 * These maintain proportions across different screen sizes
 * Adjusted for 1280x720 to look correct at 100% browser scale
 */
export const layoutDimensions = {
  // Sidebar widths - more aggressive scaling for proper fit at 1280px
  sidebarExtended: vwScale(250, 160),    // slimmer
  sidebarCollapsed: vwScale(80, 50),     // slimmer
  
  // Navbar heights - scaled down for better proportions
  navbarDesktop: vwScale(78, 44),        // slimmer
  navbarMobile: vwScale(58, 34),         // slimmer
  
  // Layout paddings - more compact at smaller screens
  layoutPaddingLarge: vwScale(30, 14),
  layoutPaddingMedium: vwScale(20, 10),
  layoutPaddingSmall: vwScale(12, 6),
};

/**
 * Responsive spacing scale
 * Uses fluid values that scale with viewport
 */
export const spacing = {
  xs: fluidValue(0.25, 0.5),    // 4px -> 8px
  sm: fluidValue(0.5, 0.75),    // 8px -> 12px
  md: fluidValue(0.75, 1),      // 12px -> 16px
  lg: fluidValue(1, 1.5),        // 16px -> 24px
  xl: fluidValue(1.5, 2),        // 24px -> 32px
  '2xl': fluidValue(2, 3),       // 32px -> 48px
  '3xl': fluidValue(3, 4),       // 48px -> 64px
  '4xl': fluidValue(4, 5),       // 64px -> 80px
};

/**
 * Fluid typography scale
 * Typography that scales smoothly between mobile and desktop
 */
export const fluidTypography = {
  // Display sizes
  display1: {
    fontSize: fluidValue(2.5, 4.5),  // 40px -> 72px
    lineHeight: '1.1',
  },
  display2: {
    fontSize: fluidValue(2, 3.75),   // 32px -> 60px
    lineHeight: '1.2',
  },
  
  // Headings
  h1: {
    fontSize: fluidValue(2, 3.5),    // 32px -> 56px
    lineHeight: '1.3',
  },
  h2: {
    fontSize: fluidValue(1.75, 3),   // 28px -> 48px
    lineHeight: '1.3',
  },
  h3: {
    fontSize: fluidValue(1.5, 2.25), // 24px -> 36px
    lineHeight: '1.4',
  },
  h4: {
    fontSize: fluidValue(1.25, 2),   // 20px -> 32px
    lineHeight: '1.4',
  },
  h5: {
    fontSize: fluidValue(1.125, 1.5), // 18px -> 24px
    lineHeight: '1.5',
  },
  h6: {
    fontSize: fluidValue(1, 1.25),   // 16px -> 20px
    lineHeight: '1.5',
  },
  
  // Body text
  body1: {
    fontSize: fluidValue(0.875, 1),  // 14px -> 16px
    lineHeight: '1.5',
  },
  body2: {
    fontSize: fluidValue(0.8125, 0.875), // 13px -> 14px
    lineHeight: '1.57',
  },
  
  // Small text
  caption: {
    fontSize: fluidValue(0.6875, 0.75), // 11px -> 12px
    lineHeight: '1.66',
  },
  overline: {
    fontSize: fluidValue(0.6875, 0.75), // 11px -> 12px
    lineHeight: '2.5',
    letterSpacing: '0.05em',
    textTransform: 'uppercase' as const,
  },
  
  // Subtitles
  subtitle1: {
    fontSize: fluidValue(0.875, 1),  // 14px -> 16px
    lineHeight: '1.5',
  },
  subtitle2: {
    fontSize: fluidValue(0.8125, 0.875), // 13px -> 14px
    lineHeight: '1.57',
  },
  
  // Button
  button: {
    fontSize: fluidValue(0.8125, 0.875), // 13px -> 14px
    lineHeight: '1.75',
  },
};

/**
 * Responsive font sizes for specific use cases
 */
export const responsiveFontSizes = {
  xs: fluidValue(0.625, 0.75),    // 10px -> 12px
  sm: fluidValue(0.75, 0.875),    // 12px -> 14px
  base: fluidValue(0.875, 1),     // 14px -> 16px
  lg: fluidValue(1, 1.125),       // 16px -> 18px
  xl: fluidValue(1.125, 1.25),    // 18px -> 20px
  '2xl': fluidValue(1.25, 1.5),   // 20px -> 24px
  '3xl': fluidValue(1.5, 1.875),  // 24px -> 30px
  '4xl': fluidValue(1.875, 2.25), // 30px -> 36px
  '5xl': fluidValue(2.25, 3),     // 36px -> 48px
  '6xl': fluidValue(3, 3.75),     // 48px -> 60px
  '7xl': fluidValue(3.75, 4.5),   // 60px -> 72px
};

/**
 * Get responsive value based on viewport
 * @param mobile - Mobile value
 * @param tablet - Tablet value
 * @param desktop - Desktop value
 * @returns Object with responsive values for MUI sx prop
 */
export const responsive = <T>(mobile: T, tablet?: T, desktop?: T) => ({
  xs: mobile,
  md: tablet ?? mobile,
  lg: desktop ?? tablet ?? mobile,
});

/**
 * Responsive container widths
 */
export const containerWidths = {
  xs: '100%',
  sm: fluidValue(36, 40),   // 576px -> 640px
  md: fluidValue(45, 48),   // 720px -> 768px
  lg: fluidValue(60, 64),   // 960px -> 1024px
  xl: fluidValue(71.25, 80), // 1140px -> 1280px
  '2xl': fluidValue(80, 96), // 1280px -> 1536px
}; 